// Der Inhalt des Explorer-Baums (docs/shell.md).
//
// Trennung wie ueberall in der Shell: diese Datei WEISS, was in den
// Sektionen lebt; Explorer.svelte weiss nur, wie man einen Baum
// zeichnet. Eine neue Domaene ist damit ein Fall hier, kein Eingriff
// in der Seitenleiste.
//
// DIE ORDNUNG: Seiten sind die Struktur, Dinge haengen darunter.
//
// Zuerst standen Dinge und Seiten als gleichrangige Gruppen
// nebeneinander -- "AKTOREN 9" und darunter "SEITEN 9", in der auch
// wieder "Aktoren" stand. Dasselbe zweimal, einmal als Liste und
// einmal als Seite (Tim 15.08.). Richtig ist die Schachtelung: die
// Seite "Aktoren" laesst sich aufklappen, DANN sieht man die einzelnen
// Aktoren. Seiten ohne Dinge (Sensoren) bleiben einfach zu -- kein
// Sonderfall, nur ein Ast ohne Blaetter.
//
// Die Schachtelung der SEITEN kommt dabei geschenkt aus routes.ts:
// /zuhause/wetter/blitze haengt unter /zuhause/wetter, weil der Pfad
// das schon sagt. Nur die DINGE muessen hier zugeordnet werden.
//
// Datenherkunft: ausschliesslich Stores, die ohnehin seit dem Boot
// laufen (docs/data-loading.md). Der Baum legt KEINEN Datenpfad an --
// er waere sonst der teuerste Teil der Oberflaeche, obwohl er nur
// Navigation ist.

import type { IconName } from "../Icon.svelte";
import { ordne, verschiebe } from "../anordnung";
import { sortierModusHalten } from "../anordnung.svelte";
import { getPref, setPref } from "../prefs.svelte";
import { getAktorenSummary } from "../aktoren-summary.svelte";
import { getClusterStats } from "../cluster-stats.svelte";
import { chatsJeRepo, imArchiv, repoLage, repoStandFuer, wannKurz, type Chat } from "../claude/claude";
import { getClaude } from "../claude-summary.svelte";
import { waehleRepo } from "../claude/baum.svelte";
import { aendere, raeumeWeg } from "../claude/chat.svelte";
import { hatAktives, type BaumKnoten, type BaumOrdner, type ZeilenStatus } from "./baum-knoten.svelte";
import { toast } from "../toast.svelte";
import type { TaskItem } from "../types";
import { getDockerHosts } from "../docker-summary.svelte";
import { isEntityDisabled } from "../entity-disables.svelte";
import { getStorageDetails } from "../storage-details.svelte";
import { zustandVon } from "../laeufe/laeufe";
import { getTasksList } from "../tasks-list.svelte";
import { navSections, registerFor, subPagesFor, type SectionId } from "../nav-sections";
import { seitenIcon } from "./panels";
import { oeffnePfad } from "./route-sync.svelte";
import { tabWurzelFuer } from "../routes";
import { aufgabenPfad, hatZeitplan } from "../aufgaben-pfad";
import { dingId, offenesDing } from "./offenes-ding.svelte";
import { istAktiv, istOffen, istPfadAktiv, istSeiteAktiv, istSeiteOffen, oeffneTab, schliesseTabsWo } from "./tabs.svelte";
import { schlaeftGerade } from "../cluster/rechner";
import { laeuftFuer } from "../laeuft.svelte";
import { ARGUS_DINGE, ARGUS_ZAHLEN } from "../../module/argus/baum";
import { MEDIEN_ZAHLEN } from "../../module/medien/baum";

// Knoten, Scharf-Zustand und hatAktives liegen ohne Stores in
// baum-knoten.svelte.ts -- das Recovery-Tool zeichnet damit denselben Baum.
export { baumScharf, hatAktives, type BaumKnoten, type BaumOrdner, type ZeilenStatus } from "./baum-knoten.svelte";

// ─── Hilfen ─────────────────────────────────────────────────────────

function prozent(anteil: number, gesamt: number): string {
  if (!gesamt) return "";
  return Math.round((anteil / gesamt) * 100) + " %";
}

/** Zustand eines ZFS-Pools auf die vier Stufen abbilden. */
function poolStatus(health: string): ZeilenStatus {
  const h = (health || "").toUpperCase();
  if (h === "ONLINE") return "ok";
  if (!h) return "off";
  // DEGRADED ist ROT, nicht gelb: das Backend fuehrt jeden Pool, der nicht
  // ONLINE ist, als kritische Meldung (pool_degraded). Gelb stand hier bis
  // 13.09.2026 -- waehrend eine Platte fehlte und der Resilver lief, trug
  // die Leiste ein gelbes "1" neben einer roten Meldung "Kritisch".
  return "bad"; // DEGRADED, FAULTED, UNAVAIL, SUSPENDED
}

/** knotenFuerPfad: der Standard-Knoten fuer eine Seite. */
function seitenKnoten(label: string, href: string): BaumKnoten {
  return {
    id: "page:" + href,
    label,
    href,
    icon: seitenIcon(href),
    oeffnen: () => oeffnePfad(href, { vorschau: true }),
    anheften: () => oeffnePfad(href),
    // Aktiv ist die Zeile, auf der man STEHT (genauer Pfad). Die Seite
    // selbst zeigt „offen", solange eines ihrer Register gewählt ist; ein
    // Register ist nie bloß „offen" -- es gibt nur einen Tab je Seite.
    offen: istRegister(href) ? false : istSeiteOffen(href),
    aktiv: istPfadAktiv(href),
  };
}

/** dingAktiv: steht man auf dieser Seite UND ist dort genau dieses Ding
 *  aufgeklappt? Für Dinge ohne eigenen Pfad (Aufgabe, Pool, Platte) --
 *  die Seite meldet es über offenes-ding. */
function dingAktiv(seite: string, id: string): boolean {
  return istPfadAktiv(seite) && offenesDing(seite) === id;
}

function istRegister(href: string): boolean {
  return tabWurzelFuer(href) !== href;
}

// ─── Dinge unter ihrer Seite ────────────────────────────────────────
//
// Je Eintrag: welcher SEITE haengen welche Dinge an. Mehr braucht es
// nicht -- die Seiten selbst und ihre Schachtelung kommen aus routes.ts.

function aktoren(): BaumKnoten[] {
  const devices = getAktorenSummary()?.devices ?? [];
  // Nur Schaltbares: eine Fernbedienung kann man nicht schalten, und
  // der Baum ist zum Hingehen da, nicht zum Vollzaehlen.
  return devices
    .filter((d) => d.kind === "light" || d.kind === "switch")
    .map((d) => {
      const pfad = "/zuhause/aktoren/" + encodeURIComponent(d.name);
      return {
        id: "aktor:" + d.name,
        label: d.name,
        icon: (d.kind === "light" ? "bulb" : "zap") as IconName,
        tail:
          d.state === "ON"
            ? d.brightness != null
              ? `an · ${Math.round((d.brightness / 254) * 100)} %`
              : "an"
            : d.state === "OFF"
              ? "aus"
              : "",
        // An/aus ist hier ein ZUSTAND, kein Befund -- den traegt der
        // Zusatztext ("an · 60 %"/"aus"). Ein Punkt erscheint nur, wenn
        // das Geraet nicht erreichbar ist; sonst saehe ein
        // ausgeschaltetes Licht mit dem Offline-Ring gestoert aus.
        status: d.available === false ? ("bad" as const) : undefined,
        oeffnen: () => oeffnePfad(pfad, { vorschau: true }),
        anheften: () => oeffnePfad(pfad),
        offen: istSeiteOffen(pfad),
        aktiv: istSeiteAktiv(pfad),
      } satisfies BaumKnoten;
    });
}

function hosts(): BaumKnoten[] {
  // Hosts kommen aus cluster-stats: hosts-summary ist nur das Aggregat
  // fuer die Dashboard-Karte und kennt die einzelnen Hosts gar nicht.
  return (getClusterStats() ?? [])
    .flatMap((c) => c.hosts)
    .map((h) => {
      const pfad = "/infra/cluster/" + encodeURIComponent(h.id);
      // Was wartet an diesem Host? Verfuegbare Updates zaehlen einzeln,
      // ein faelliger Neustart als einer. Immer nur WARN, nie rot:
      // ein Update ist im Homelab ein Handgriff, kein Vorfall -- rot
      // bleibt fuer Dinge, die kaputt sind (Tim 25.08.).
      const upd = h.updates;
      const punkte = (upd?.pending ?? 0) + (upd?.reboot_required ? 1 : 0);
      return {
        id: "host:" + h.id,
        label: h.name || h.id,
        icon: "server" as IconName,
        punkte: punkte || undefined,
        punkteTon: "warn" as const,
        // Der Warnfall ist "lebt, aber nicht erreichbar": dann
        // scheitern Backups und Eingriffe, obwohl alles gruen aussieht.
        // Ausser bei Rechnern, die schlafen duerfen (MacBook): dort ist
        // "SSH antwortet nicht" der Schlaf, kein Befund (Tim 29.09.).
        tail: schlaeftGerade(h, !!h.sleep_ok) ? "schläft" : h.online === false ? "offline" : h.ssh_reachable === false ? "kein SSH" : "",
        status:
          schlaeftGerade(h, !!h.sleep_ok) || h.online === false
            ? "off"
            : h.ssh_reachable === false
              ? "warn"
              : h.online
                ? "ok"
                : "off",
        oeffnen: () => oeffnePfad(pfad, { vorschau: true }),
        anheften: () => oeffnePfad(pfad),
        // Der Rechner ist ein Register der Cluster-Seite (routes.ts,
        // tabWurzel), kein eigener Tab: markiert ist nur der, dessen
        // Zeile gerade offen steht (genauer Pfad). Mit dem Seiten-
        // Vergleich wären alle neun zugleich markiert, sobald „Cluster"
        // offen ist -- und „offen" ist ein Register nie (wie oben).
        offen: false,
        aktiv: istPfadAktiv(pfad),
      } satisfies BaumKnoten;
    });
}

/** Container haengen unter ihrem HOST, der Host unter der Docker-Seite:
 *  drei Ebenen, weil die Zugehoerigkeit sonst in einer Spalte
 *  verschwinden wuerde. */
function dockerHosts(): BaumKnoten[] {
  return getDockerHosts()
    .filter((dh) => dh.containers?.length)
    .map((dh) => ({
      id: "dockerhost:" + dh.host_id,
      label: dh.host_name || dh.host_id,
      icon: "server" as IconName,
      tail: String(dh.containers.length),
      status: (dh.online ? "ok" : "off") as ZeilenStatus,
      // Ein Host-Knoten ist hier nur ein Behaelter -- er zeigt die
      // Docker-Seite, gefiltert waere schoener, gibt es aber nicht.
      oeffnen: () => oeffnePfad("/infra/docker", { vorschau: true }),
      anheften: () => oeffnePfad("/infra/docker"),
      offen: false,
      aktiv: false,
      kinder: dh.containers.map((c) => ({
        id: `container:${dh.host_id}:${c.name}`,
        label: c.name,
        icon: "container" as IconName,
        // Der Stack steht nur dran, wenn er etwas HINZUFUEGT: bei
        // Ein-Container-Stacks heisst er wie der Container selbst
        // ("npm npm"), und das ist keine Information, nur Rauschen.
        tail: c.stack && c.stack !== c.name ? c.stack : "",
        status: (c.state === "running" ? "ok" : c.state === "exited" ? "off" : "warn") as ZeilenStatus,
        // Ein Container ist Tiefe, keine Seite: er oeffnet als eigener
        // Tab-Typ -- derselbe, den die Docker-Seite benutzt.
        oeffnen: () =>
          oeffneTab("docker-container", { hostId: dh.host_id, name: c.name }, { vorschau: true }),
        anheften: () => oeffneTab("docker-container", { hostId: dh.host_id, name: c.name }),
        offen: istOffen("docker-container", { hostId: dh.host_id, name: c.name }),
        aktiv: istAktiv("docker-container", { hostId: dh.host_id, name: c.name }),
      })),
    }));
}

function pools(): BaumKnoten[] {
  const snap = getStorageDetails();

  // Kranke Platten haengen unter IHREM Pool -- die Zeile "pool" auf
  // homeserver2 traegt dann die 3, und die Sektion faerbt sich rot.
  // Warum ueberhaupt: am 06.09. standen drei Platten kritisch, und
  // ausserhalb der geoeffneten Storage-Seite sah man davon nichts
  // (Tim: "niemand merkt es"). Der Zustand kam aus dem Collector,
  // aber er kam nirgends AN.
  //
  // Nur crit/warn zaehlen: "bekannt·stabil" ist geackt und wartet auf
  // niemanden, "stillgelegt" ist eine Entscheidung, kein offener Punkt.
  const proPool = new Map<string, BaumKnoten[]>();
  const ohnePool: BaumKnoten[] = [];
  for (const d of snap?.disks ?? []) {
    if (d.prefail_state !== "crit" && d.prefail_state !== "warn") continue;
    const pfad = `/infra/storage?disk=${encodeURIComponent(d.host + "/" + d.name)}`;
    const knoten: BaumKnoten = {
      id: `disk:${d.host}/${d.name}`,
      label: d.name,
      icon: "hdd" as IconName,
      tail: d.model || d.host,
      status: (d.prefail_state === "crit" ? "bad" : "warn") as ZeilenStatus,
      punkte: 1,
      punkteTon: d.prefail_state === "crit" ? ("bad" as const) : ("warn" as const),
      oeffnen: () => oeffnePfad(pfad, { vorschau: true }),
      anheften: () => oeffnePfad(pfad),
      offen: false,
      aktiv: dingAktiv("/infra/storage", dingId.platte(d.host, d.name)),
    };
    if (d.pool) {
      const schluessel = `${d.host}/${d.pool}`;
      const liste = proPool.get(schluessel);
      if (liste) liste.push(knoten);
      else proPool.set(schluessel, [knoten]);
    } else {
      ohnePool.push(knoten);
    }
  }

  // Ein Pool heisst NICHT eindeutig: "cache" und "pool" gibt es auf
  // mehreren Hosts. Seine Identitaet ist Host + Name -- und weil der
  // Name allein damit mehrdeutig ist, steht der Host in der Zeile.
  const poolKnoten = (snap?.pools ?? []).map((p) => {
    const platten = proPool.get(`${p.host_id}/${p.name}`);
    if (platten) proPool.delete(`${p.host_id}/${p.name}`);
    return {
      id: `pool:${p.host_id}/${p.name}`,
      label: p.name,
      icon: "hdd" as IconName,
      tail:
        p.host_id +
        " · " +
        (poolStatus(p.health) === "ok"
          ? prozent(p.usable_used || p.alloc, p.usable_size || p.size)
          : p.health),
      status: poolStatus(p.health),
      // Stillgelegt (Wartung, geplanter Tausch) zaehlt nicht -- dieselbe
      // Regel wie im Collector, der dann auch keine Meldung schreibt.
      punkte:
        poolStatus(p.health) === "ok" || isEntityDisabled("pool", `${p.host_id}:${p.name}`)
          ? undefined
          : 1,
      punkteTon: poolStatus(p.health) === "bad" ? ("bad" as const) : ("warn" as const),
      kinder: platten,
      // Auf DIESEN Pool: seine Zeile unter „Platz" klappt auf (Storage
      // liest ?pool=). Der Schlüssel dort ist host/name, nicht host_id.
      oeffnen: () => oeffnePfad(`/infra/storage?pool=${encodeURIComponent(p.host + "/" + p.name)}`, { vorschau: true }),
      anheften: () => oeffnePfad(`/infra/storage?pool=${encodeURIComponent(p.host + "/" + p.name)}`),
      offen: false,
      aktiv: dingAktiv("/infra/storage", dingId.pool(p.host, p.name)),
    } satisfies BaumKnoten;
  });

  // Platten ohne (bekannten) Pool stehen daneben statt zu verschwinden:
  // eine Systemplatte oder eine gerade aus dem Pool geflogene Platte
  // ist genau der Fall, den man NICHT uebersehen will.
  return [...poolKnoten, ...[...proPool.values()].flat(), ...ohnePool];
}

/** Hat der letzte Lauf der Aufgabe gescheitert? Den Status "error" gibt es
 *  nicht, die Datenbank sagt "failed" (laeufe.ts: zustandVon) -- bis
 *  02.11.2026 wurde mit "error" verglichen, fehlgeschlagene Aufgaben blieben
 *  im Baum unmarkiert. */
function fehlgeschlagen(t: TaskItem): boolean {
  return !!t.last_run && zustandVon(t.last_run.status) === "failed";
}

function tasks(): BaumKnoten[] {
  // Was nicht laeuft, steht oben: eine haengende Aufgabe ist der Grund,
  // warum man hier ueberhaupt nachsieht.
  return [...(getTasksList() ?? [])]
    .sort((a, b) => {
      const av = fehlgeschlagen(a) ? 0 : 1;
      const bv = fehlgeschlagen(b) ? 0 : 1;
      if (av !== bv) return av - bv;
      return a.name.localeCompare(b.name);
    })
    .map((t) => {
      // Ohne Zeitplan steht die Aufgabe bei den Werkzeugen, nicht hier.
      const pfad = aufgabenPfad(t);
      return {
        id: "task:" + t.id,
        label: t.name,
        icon: "checks" as IconName,
        tail: t.enabled ? "" : "aus",
        punkte: t.enabled && fehlgeschlagen(t) ? 1 : undefined,
        punkteTon: "bad" as const,
        status: (!t.enabled
          ? "off"
          : fehlgeschlagen(t)
            ? "bad"
            : t.last_run?.status === "running"
              ? "warn"
              : "ok") as ZeilenStatus,
        oeffnen: () => oeffnePfad(pfad, { vorschau: true }),
        anheften: () => oeffnePfad(pfad),
        // Markiert ist die Aufgabe, deren Zeile auf der Aufgaben-Seite
        // offen steht. Ohne Zeitplan steht sie woanders (Werkzeuge,
        // Backups) -- dort gibt es diese Rückmeldung nicht.
        offen: false,
        aktiv: hatZeitplan(t) && dingAktiv("/system/tasks", dingId.aufgabe(t.id)),
      } satisfies BaumKnoten;
    });
}

/** Der Bereich Claude (docs/claude.md) hat keine Seiten-Struktur, sondern
 *  Chats je Repo -- „im Baum die Chats und den Verbrauch ähnlich wie in
 *  VSCode" (Tim 16.10.2026). Das ist der Abschnitt „Chats"; Änderungen und
 *  Verbrauch stehen in eigenen Abschnitten darunter (explorer-zusatz.ts).
 *  Jedes Repo steht da, auch ohne Chat; ein Klick wählt es für „Änderungen". */
function claudeBaum(): BaumKnoten[] {
  const a = getClaude();
  if (!a) return [];
  const jetzt = new Date();
  return chatsJeRepo(a.stand)
    .map((g) => {
      // Das Archiv je Repo (Tim 19.10.2026): von Hand hineingelegt oder
      // seit 14 Tagen still -- zugeklappt unter „Archiv" (imArchiv).
      const aktuell = g.chats.filter((c) => !imArchiv(c, jetzt));
      const archiv = g.chats.filter((c) => imArchiv(c, jetzt));
      // Rückmeldungen aus home (docs/claude.md, Tim 24.10.2026: „damit die
      // Chats vom home nicht den Baum sprengen" / „muss noch in Rückmeldungen
      // verschoben werden"): alle zugeklappt unter „Rückmeldungen". Ebenso
      // die tägliche Vorprüfung der Argus-Bilder (docs/argus-modul.md, Tim
      // 03.11.2026: „Der Chat entsteht dann nicht jeden Tag neu und wird dann
      // gesammelt?") unter „Vorprüfung". Wartet einer auf Tim, zählt die
      // Marke am Ordner hoch (zaehleHoch).
      const gesammelt = (c: Chat) => SAMMELORDNER.some((o) => o.gehoert(c));
      const kinder: BaumKnoten[] = aktuell.filter((c) => !gesammelt(c)).map((c) => chatKnoten(c, jetzt));
      for (const o of SAMMELORDNER) {
        const drin = aktuell.filter(o.gehoert);
        if (!drin.length) continue;
        const arbeitet = drin.some((c) => c.zustand === "arbeitet");
        kinder.push(sammelKnoten(o.id + ":" + g.repo, o.label, arbeitet ? "arbeitet" : String(drin.length), o.icon, drin.map((c) => chatKnoten(c, jetzt))));
      }
      if (archiv.length) kinder.push(sammelKnoten("archiv:" + g.repo, "Archiv", String(archiv.length), "archive", archiv.map((c) => chatKnoten(c, jetzt))));
      // Am Repo: was noch nicht gesichert ist. Nicht gepushte Commits sind
      // ein Handgriff (gelbe Marke), offene Dateien Arbeit im Gang (Text).
      const rs = repoStandFuer(a.stand, g.repo);
      const lage = repoLage(rs);
      return {
        id: "repo:" + g.repo,
        label: g.name,
        icon: "folder" as IconName,
        tail: lage.length ? (rs!.voraus > 0 ? `${rs!.voraus} nicht gepusht` : `${rs!.offen_zahl} offen`) : kinder.length ? undefined : "kein Chat",
        status: rs && rs.voraus > 0 ? ("warn" as const) : undefined,
        punkte: rs && rs.voraus > 0 ? 1 : undefined,
        punkteTon: "warn" as const,
        standardOffen: true,
        oeffnen: () => {
          waehleRepo(g.repo);
          oeffnePfad("/claude", { vorschau: true });
        },
        anheften: () => oeffnePfad("/claude"),
        offen: false,
        aktiv: false,
        kinder,
      } satisfies BaumKnoten;
    });
}

function archiviere(id: string, rein: boolean): void {
  aendere(id, { archiviert: rein }).catch((e) => toast.error(e instanceof Error ? e.message : String(e)));
}

/** Chats, die das Homelab selbst anlegt, zugeklappt in je einem Ordner:
 *  Rückmeldungen aus home (internal/homerueck) und die Vorprüfung der
 *  Argus-Bilder (internal/argus vorpruefenStarten). */
const SAMMELORDNER: { id: string; label: string; icon: IconName; gehoert: (c: Chat) => boolean }[] = [
  { id: "rueckmeldungen", label: "Rückmeldungen", icon: "message", gehoert: (c) => (c.bezug ?? "").startsWith("home-rueckmeldung:") },
  { id: "vorpruefung", label: "Vorprüfung", icon: "image", gehoert: (c) => (c.bezug ?? "").startsWith("argus-vorpruefen:") },
];

function sammelKnoten(id: string, label: string, tail: string, icon: IconName, kinder: BaumKnoten[]): BaumKnoten {
  return {
    id,
    label,
    icon,
    tail,
    oeffnen: () => oeffnePfad("/claude", { vorschau: true }),
    anheften: () => oeffnePfad("/claude"),
    offen: false,
    aktiv: false,
    kinder,
  };
}


function chatKnoten(c: Chat, jetzt: Date): BaumKnoten {
  const pfad = "/claude/" + c.id;
  const wartet = c.zustand === "wartet";
  return {
    id: "chat:" + c.id,
    label: c.titel,
    icon: "message" as IconName,
    // Läuft = Akzent wie der Drucker, wartet = gelber Punkt und Marke (ein
    // Handgriff, der bis an das Symbol der Leiste hochzählt), abgebrochen
    // = rot; sonst wann zuletzt.
    tail: c.zustand === "arbeitet" ? "arbeitet" : wartet ? "wartet" : c.zustand === "fehler" ? "abgebrochen" : wannKurz(c.zuletzt, jetzt),
    status: c.zustand === "arbeitet" ? "laeuft" : wartet ? "warn" : c.zustand === "fehler" ? "bad" : undefined,
    punkte: wartet ? Math.max(1, c.offen?.length ?? 0) : undefined,
    punkteTon: "warn",
    ohnePunkt: true,
    oeffnen: () => oeffnePfad(pfad, { vorschau: true }),
    anheften: () => oeffnePfad(pfad),
    offen: istSeiteOffen(pfad),
    aktiv: istPfadAktiv(pfad),
    // Ins Archiv und Wegräumen nur, wenn gerade nicht gearbeitet wird (Tim
    // 16.10.2026): einen arbeitenden oder wartenden Chat räumt man nicht
    // aus Versehen weg.
    aktionen:
      c.zustand === "arbeitet" || wartet
        ? undefined
        : [
            imArchiv(c, jetzt)
              ? { icon: "archive-restore" as IconName, label: "Aus dem Archiv holen", ausfuehren: () => archiviere(c.id, false) }
              : { icon: "archive" as IconName, label: "Ins Archiv", ausfuehren: () => archiviere(c.id, true) },
            {
              icon: "trash" as IconName,
              label: "Chat wegräumen",
              scharf: "Wirklich wegräumen? Der Verlauf hier geht verloren. Nochmal klicken",
              ausfuehren: () => {
                raeumeWeg(c.id)
                  .then(() => schliesseTabsWo((t) => t.typ === "page" && String(t.params.path ?? "") === pfad))
                  .catch((e) => toast.error(e instanceof Error ? e.message : String(e)));
              },
            },
          ],
  };
}

/** Welche Seite traegt welche Dinge. Der einzige Ort, an dem der Baum
 *  Domaenen-Wissen braucht -- alles andere leitet er aus routes.ts ab. */
const DINGE: Record<string, () => BaumKnoten[]> = {
  "/zuhause/aktoren": aktoren,
  "/infra/cluster": hosts,
  "/infra/docker": dockerHosts,
  "/infra/storage": pools,
  "/system/tasks": tasks,
  // Module bringen ihre Dinge selbst mit (module/<name>/baum.ts).
  ...ARGUS_DINGE,
};

/** Zahl oder Wort rechts an einer Seite, wenn dort nichts läuft (Argus:
 *  „212 offen" an „Bilder prüfen"). Module bringen ihre selbst mit. */
const ZAHLEN: Record<string, () => string> = {
  ...ARGUS_ZAHLEN,
  ...MEDIEN_ZAHLEN,
};

// ─── Aufbau ─────────────────────────────────────────────────────────

/** zaehleHoch summiert die offenen Punkte von unten nach oben.
 *
 *  Der Sinn: ein zugeklappter Ast darf nichts verbergen. Steht am Host
 *  "forge" ein faelliger Neustart, muss man das an "Cluster" und am
 *  Sektions-Symbol sehen, ohne vorher irgendwo hinzuklicken -- sonst
 *  findet man offene Punkte nur, wenn man sie ohnehin schon sucht
 *  (Tim 15.08.). */
function zaehleHoch(knoten: BaumKnoten[]): Offene {
  const gesamt: Offene = { punkte: 0 };
  for (const k of knoten) {
    const kinder = k.kinder?.length ? zaehleHoch(k.kinder) : { punkte: 0 };
    k.summe = (k.punkte ?? 0) + kinder.punkte;
    k.summeTon = schwerer(k.punkte ? k.punkteTon : undefined, kinder.ton);
    gesamt.punkte += k.summe;
    gesamt.ton = schwerer(gesamt.ton, k.summeTon);
  }
  return gesamt;
}

/** Offene Punkte samt Stufe. Die Stufe ist der Grund, warum die
 *  Leiste ueberhaupt auffaellt: eine Zahl allein liest man als
 *  "irgendwann mal", rot liest man heute (Tim 06.09.). */
export interface Offene {
  punkte: number;
  ton?: "warn" | "bad";
}

function schwerer(a?: "warn" | "bad", b?: "warn" | "bad"): "warn" | "bad" | undefined {
  if (a === "bad" || b === "bad") return "bad";
  return a ?? b;
}

/** offenePunkte: die Summe einer ganzen Sektion, fuer das Symbol in
 *  der Aktivitaetsleiste. */
export function offenePunkte(id: SectionId): Offene {
  return zaehleHoch(baueBaum(id));
}

/** eindeutig sichert die ID-Zusage des Baums ab.
 *
 *  Warum das hier steht: die Seitenleiste rendert mit `(k.id)` als
 *  Schluessel, und ein doppelter Schluessel ist in Svelte kein
 *  Schoenheitsfehler, sondern ein harter Fehler, der die GANZE App
 *  abraeumt (14.08.: zwei Hosts mit einem Pool namens "cache" --
 *  weisser Bildschirm). Eine Oberflaeche darf nicht daran sterben,
 *  dass zwei Dinge in der Welt gleich heissen.
 *
 *  Der eigentliche Fix ist immer eine richtige Identitaet (Host +
 *  Name). Diese Wache faengt nur den Fall, an den beim naechsten Mal
 *  niemand gedacht hat -- und sagt in der Konsole, wo. */
function eindeutig(knoten: BaumKnoten[], gesehen = new Set<string>()): BaumKnoten[] {
  for (const k of knoten) {
    if (gesehen.has(k.id)) {
      console.warn(`shell/tree: doppelte Knoten-ID "${k.id}"`);
      k.id = k.id + "~" + gesehen.size;
    }
    gesehen.add(k.id);
    if (k.kinder?.length) eindeutig(k.kinder, gesehen);
  }
  return knoten;
}

/** baueBaum liefert den Baum der Sektion. Wird in einem $derived
 *  gerufen -- die Store-Zugriffe machen ihn damit von selbst reaktiv. */
export function baueBaum(id: SectionId): BaumKnoten[] {
  const baum = ordneEbene(id, baueRoh(id), "");
  zaehleHoch(baum);
  return baum;
}

// ─── Eigene Reihenfolge ─────────────────────────────────────────────
//
// Tim 16.10.2026: „Die Elemente im Baum sollte man noch anordnen können."
// Je EBENE eine Reihenfolge (die Seiten eines Bereichs, die Rechner unter
// Cluster, die Container eines Hosts), gezogen wird nur unter Geschwistern.
// Die Rechnung ist dieselbe wie bei den Karten der Seiten (lib/anordnung.ts):
// Neues kommt hinter seinen Vorgänger, gerade Fehlendes behält seinen Platz.
// Anders als dort ordnen wir hier die DATEN -- der Baum ist ohnehin eine
// Liste, und so folgt auch die Telefon-Übersicht (BereichsUebersicht).
//
// Ohne eigene Reihenfolge gilt die Ordnung von oben (Aufgaben: Fehler
// zuerst). Mit eigener gilt die eigene, auch für eine Aufgabe mit Fehler --
// die rote Zahl am Ast sagt es trotzdem.

function ordnungsSchluessel(sektion: SectionId, eltern: string): string {
  return `card_order_baum:${sektion}/${eltern}`;
}

function ordneEbene(sektion: SectionId, knoten: BaumKnoten[], eltern: string): BaumKnoten[] {
  const aus = ordne(getPref<string[]>(ordnungsSchluessel(sektion, eltern)), knoten);
  for (const k of aus) if (k.kinder?.length) k.kinder = ordneEbene(sektion, k.kinder, k.id);
  return aus;
}

/** Sortiermodus des Baums. Vom Explorer einmal angelegt (der Modus hält
 *  sich per $effect, darum in der Initialisierung einer Komponente). */
export class BaumOrdnen implements BaumOrdner {
  /** Ebene im Sortiermodus (Kennung des Elternknotens, "" = oberste),
   *  sonst null */
  ebene = $state<string | null>(null);
  #sektion: () => SectionId;

  constructor(sektion: () => SectionId) {
    this.#sektion = sektion;
    $effect(() => {
      const e = this.ebene;
      if (e === null) return;
      return sortierModusHalten(`[data-reorder-group="${this.gruppe(e)}"]`, () => (this.ebene = null));
    });
  }

  /** Zieh-Gruppe der Ebene (press-reorder: data-reorder-group) */
  gruppe(eltern: string): string {
    return `baum:${this.#sektion()}/${eltern}`;
  }

  angepasst(eltern: string): boolean {
    return (getPref<string[]>(ordnungsSchluessel(this.#sektion(), eltern))?.length ?? 0) > 0;
  }

  /** aktuell = die Kennungen der Ebene in ihrer jetzigen Reihenfolge */
  verschiebe(eltern: string, aktuell: string[], von: string, nach: string): void {
    const key = ordnungsSchluessel(this.#sektion(), eltern);
    const neu = verschiebe(getPref<string[]>(key), aktuell, von, nach);
    if (neu) setPref(key, neu);
  }

  zuruecksetzen(eltern: string): void {
    setPref(ordnungsSchluessel(this.#sektion(), eltern), null);
  }
}

function baueRoh(id: SectionId): BaumKnoten[] {
  if (id === "claude") return eindeutig(claudeBaum());
  const sec = navSections.find((s) => s.id === id);
  if (!sec) return [];

  // Register (Netzwerk → Verlauf) sind Seiten wie andere: ihr Pfad liegt
  // unter ihrer Seite, das Schachteln unten hängt sie dort ein -- nach
  // den Dingen.
  const seiten = [...subPagesFor(sec), ...registerFor(sec)].filter((p) => p.href !== sec.href);

  // Knoten je Seite anlegen und die Dinge anhaengen.
  const proPfad = new Map<string, BaumKnoten>();
  for (const p of seiten) {
    const k = seitenKnoten(p.label, p.href);
    // Läuft hier gerade etwas (Drucker druckt), steht es an der Zeile:
    // Fortschritt als Zahl in Akzent, die Zeile füllt sich von links.
    // Kein Punkt: Punkte heißen im Baum Handgriff oder Defekt, ein
    // Akzent-Punkt davor las sich wie eine Warnung (Tim 15.10.2026: „wirkt
    // fehl am Platz"). Wartet es auf einen Handgriff, dann eben doch der
    // gelbe Punkt -- wie jede andere Zeile, die etwas will.
    const lauf = laeuftFuer(p.href);
    if (lauf) {
      k.tail = lauf.fortschritt != null ? `${lauf.fortschritt} %` : lauf.ton === "warn" ? "wartet" : lauf.was;
      k.status = lauf.ton === "warn" ? "warn" : lauf.ton === "laeuft" ? "laeuft" : undefined;
      if (lauf.ton === "laeuft" && lauf.fortschritt != null) k.fortschritt = lauf.fortschritt;
    }
    if (!k.tail) k.tail = ZAHLEN[p.href]?.() || undefined;
    const dinge = DINGE[p.href]?.() ?? [];
    if (dinge.length) k.kinder = dinge;
    // Markiert ist die Zeile, auf der man STEHT -- und das ist das
    // aufgeklappte Ding, nicht mehr die Seite. Nur wenn der Baum das Ding
    // auch führt: eine unauffällige Platte hat hier keine Zeile, dann
    // bleibt die Seite markiert.
    if (k.aktiv && hatAktives(dinge)) k.aktiv = false;
    proPfad.set(p.href, k);
  }

  // Schachteln nach Pfad: die laengste andere Seite, die ein echtes
  // Praefix ist, wird zum Elternteil (/zuhause/wetter/blitze landet so
  // unter /zuhause/wetter, ohne dass es hier jemand aufschreiben muss).
  const wurzel: BaumKnoten[] = [];
  for (const p of seiten) {
    const knoten = proPfad.get(p.href)!;
    // Ein Register hängt an seiner Seite (tabWurzel), auch wenn sein Pfad
    // nicht darunter liegt (/system/db gehört zu /system/werkzeuge).
    let elternPfad = p.tabWurzel && proPfad.has(p.tabWurzel) ? p.tabWurzel : "";
    for (const q of p.tabWurzel ? [] : seiten) {
      if (q.href === p.href) continue;
      if (!p.href.startsWith(q.href + "/")) continue;
      if (q.href.length > elternPfad.length) elternPfad = q.href;
    }
    const eltern = elternPfad ? proPfad.get(elternPfad) : undefined;
    if (eltern) {
      // Unterseiten stehen NACH den Dingen: die Dinge sind der Inhalt
      // der Seite, die Unterseiten sind Nachbarn eine Ebene tiefer.
      // Unter "Storage" heißt "Storage-Alarme" nur "Alarme" -- der Ast
      // sagt schon, wessen Alarme.
      if (knoten.label.startsWith(eltern.label + "-")) knoten.label = knoten.label.slice(eltern.label.length + 1);
      (eltern.kinder ??= []).push(knoten);
    } else {
      wurzel.push(knoten);
    }
  }

  return eindeutig(wurzel);
}
